<template>
    <div
        :style="{
            background: $vuetify.theme.themes[theme].background,
        }"
        class="mt-3"
    >
        <p v-if="sessions.length === 0 && !loading" class="py-5 px-5">
            {{
                $t('message.noSessionsData', {
                    category: selectedTab,
                    sessionType:
                        selectedView === 'class' ? 'classes' : 'courses',
                })
            }}
        </p>
        <div v-else>
            <div
                class="tw-hidden md:tw-block tw-px-4"
                :style="{
                    borderBottom: `2px solid ${$vuetify.theme.themes[theme].dilutedPrimary}`,
                }"
            >
                <div
                    class="tw-flex tw-items-center tw-justify-between tw-w-full"
                >
                    <div class="tw-w-1/6">
                        <div class="tw-py-2 tw-text-primary tw-font-bold">
                            {{ $t('message.time') }}
                        </div>
                    </div>
                    <div class="tw-w-1/6">
                        <div class="tw-py-2 tw-text-primary tw-font-bold">
                            {{ $t('message.class') }}
                        </div>
                    </div>
                    <div class="tw-w-1/6">
                        <div class="tw-py-2 tw-text-primary tw-font-bold">
                            {{ $t('message.instructor') }}
                        </div>
                    </div>
                    <div class="tw-w-1/6">
                        <div class="tw-py-2 tw-text-primary tw-font-bold">
                            {{ $t('message.location') }}
                        </div>
                    </div>
                    <div class="tw-w-1/6"></div>
                </div>
            </div>
        </div>
        <ClassViewWithAttendee
            :studio="studio"
            :classes="sessions"
            :cancelUpcomingBtn="selectedTab === 'upcoming'"
            :cancelWaitlistBtn="selectedTab === 'waitlist'"
            :changeAttendeeBtn="
                selectedTab === 'upcoming' && selectedView === 'class'
            "
            :isClickable="['upcoming', 'waitlist'].includes(selectedTab)"
            @cancelled="handleCancelledSuccess"
            @cancelFeePaymentError="handleCancelFeePaymentError"
            @assignFamilyUser="handleAssignFamilyUserSuccess"
            @removed="handleWaitlistSuccess"
            :accounts="accounts"
            @refresh="refreshList"
        />
        <v-skeleton-loader class="mt-5" v-if="loading" type="card" />
        <div class="d-flex justify-center" v-if="showViewMoreBtn">
            <v-btn
                text
                class="primary text-capitalize mt-2"
                @click="getMorePastClassesAndCourseSessions"
                :loading="viewMoreLoading"
                >{{ $t('message.classesViewMore') }}</v-btn
            >
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
    getUserUpcomingClasses,
    getUserPastClasses,
    getUserWaitlist,
} from '@/util/cloudFunctions/classesFunctions'
import {
    getUpcomingUserCourseSessions,
    getPastUserCourseSessions,
} from '@/util/cloudFunctions/coursesFunctions'
import ClassViewWithAttendee from '@/components/myclasses/ClassViewWithAttendee.vue'
import BackButton from '@/components/shared/BackButton.vue'
import { AUTHENTICATION_MODULE_NAME } from '@/store/moduleNames'
import { STUDIO_MODULE_NAME } from '@/store/moduleNames'
import {
    dateStringFromTimestamp,
    timeStringFromTimestamp,
    convertDateFormat,
} from '@/util/dateformat.js'
import { getUserBookings } from '@/modules/subAccounts/api/subAccountsApi'
import { useToast } from '@/components/base/toast/composable/useToast'

export default {
    components: {
        ClassViewWithAttendee,
        BackButton,
    },
    created() {
        this.getAllClassAndCourseSessions()
    },
    props: {
        selectedTab: {
            type: String,
            default: 'upcoming',
        },
        selectedView: {
            type: String,
            default: 'class',
        },
        accounts: {
            type: Array,
            default: [],
        },
        selectedAccountId: {
            type: String,
            default: null,
        },
    },
    setup() {
        const { showErrorToast, showSuccessToast, showInfoToast } = useToast()
        return {
            showErrorToast,
            showSuccessToast,
            showInfoToast,
        }
    },
    data() {
        return {
            upcomingClasses: [],
            upcomingCourses: [],
            waitlistedClasses: [],
            pastClasses: [],
            pastCourses: [],
            cancelledClasses: [],
            loading: false,
            viewMoreLoading: false,
            bookBtn: false,
            lastPastClassId: '',
            lastPastCourseId: '',
            cancelFeePaymentError: '',
            sessions: [],
        }
    },
    computed: {
        ...mapGetters({
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
        theme() {
            return this.$vuetify.theme.dark ? 'dark' : 'light'
        },
        showViewMoreBtn() {
            return (
                this.selectedTab === 'past' &&
                (this.selectedView === 'class'
                    ? Boolean(this.lastPastClassId)
                    : Boolean(this.lastPastCourseId))
            )
        },
    },
    watch: {
        selectedView(newVal) {
            this.sessions = this.getCurrentSessions()
        },
        selectedTab(newVal) {
            this.sessions = this.getCurrentSessions()
        },
        selectedAccountId(newVal) {
            this.refreshList()
        },
    },
    methods: {
        refreshList() {
            this.getAllClassAndCourseSessions()
        },
        async getAllClassAndCourseSessions() {
            this.loading = true
            try {
                await Promise.all([
                    this.getUpcomingClassesAndCourseSessions(),
                    this.getWaitlistClasses(),
                    this.getPastClassesAndCourseSessions(),
                    this.getCancelledClasses(),
                ])
                this.sessions = this.getCurrentSessions()
            } catch (error) {
                this.showErrorToast('Failed to fetch classes and courses')
            } finally {
                this.loading = false
            }
        },
        getCurrentSessions() {
            if (this.selectedTab === 'upcoming') {
                return this.selectedView === 'class'
                    ? this.upcomingClasses
                    : this.upcomingCourses
            } else if (this.selectedTab === 'waitlist') {
                return this.waitlistedClasses
            } else if (this.selectedTab === 'past') {
                return this.selectedView === 'class'
                    ? this.pastClasses
                    : this.pastCourses
            } else if (this.selectedTab === 'cancelled') {
                return this.cancelledClasses
            }
            return []
        },
        formatArrayToObject(classes, formattedObject) {
            for (const item of classes) {
                // start date of the class
                const classDate = item.classDate

                const dateKey = dateStringFromTimestamp(
                    classDate,
                    this.studio.timezone,
                    'YYYY-MM-DD'
                )
                if (!formattedObject.has(dateKey)) {
                    formattedObject.set(dateKey, [])
                }

                item.timeStart = timeStringFromTimestamp(
                    classDate,
                    this.studio.timezone
                )
                item.timeEnd = timeStringFromTimestamp(
                    item.endTimestamp,
                    this.studio.timezone
                )
                formattedObject.get(dateKey).push(item)
            }
            return formattedObject
        },
        async getUserBookings(selectedTab, selectedView, getMore = false) {
            const request = {
                status: selectedTab,
                type: selectedView,
                studioId: this.studio.id,
            }

            if (getMore) {
                request.lastDocId =
                    selectedView === 'class'
                        ? this.lastPastClassId
                        : this.lastPastCourseId
            }

            if (
                this.selectedAccountId &&
                this.selectedAccountId !== this.user.id
            ) {
                request.subAccountId = this.selectedAccountId
            }

            const result = await getUserBookings(request)
            if (result.success) {
                // update last doc id
                if (selectedTab === 'past') {
                    if (selectedView === 'class') {
                        this.lastPastClassId =
                            result.docs[result.docs.length - 1]?.id || ''
                    } else {
                        this.lastPastCourseId =
                            result.docs[result.docs.length - 1]?.id || ''
                    }
                }

                // group by date
                const formattedObj = new Map()
                if (selectedView === 'class') {
                    this.formatClasses(result, formattedObj)
                } else {
                    this.formatCourses(result, formattedObj)
                }

                // sort by date and transform to array
                const sortDesc = (a, b) =>
                    new Date(b).getTime() - new Date(a).getTime()
                const sortAsc = (a, b) =>
                    new Date(a).getTime() - new Date(b).getTime()
                const sortFunc = selectedTab === 'past' ? sortDesc : sortAsc
                const sortedDates = Array.from(formattedObj.keys()).sort(
                    sortFunc
                )
                const resultSessions = []
                sortedDates.forEach((date) => {
                    resultSessions.push({
                        date: convertDateFormat(date),
                        item: formattedObj.get(date),
                    })
                })
                return resultSessions
            } else {
                return []
            }
        },
        formatClasses(res, formattedObject) {
            if (res.success) {
                return this.formatArrayToObject(res.docs, formattedObject)
            } else {
                return []
            }
        },
        formatCourses(res, formattedObject) {
            if (res.success) {
                return this.formatCourseRegistrationsToObject(
                    res.docs,
                    formattedObject
                )
            } else {
                return []
            }
        },
        formatCourseRegistrationsToObject(courses, formattedObject) {
            for (const course of courses) {
                this.formatCourseRegistrationToObject(course, formattedObject)
            }
            return formattedObject
        },
        formatCourseRegistrationToObject(course, formattedObject) {
            const {
                courseName,
                courseOfferingId,
                courseOfferingImage,
                studioId,
                location,
                instructor,
                fullName,
            } = course
            const item = {
                courseName,
                courseOfferingId,
                courseOfferingImage,
                studioId,
                location,
                instructor,
                fullName,
            }
            const itemWithTiming = {
                ...item,
                name: courseName,
                classId: course.id,
            }
            const dateKey = dateStringFromTimestamp(
                course.startTimestamp,
                this.studio.timezone,
                'YYYY-MM-DD'
            )
            if (!formattedObject.has(dateKey)) {
                formattedObject.set(dateKey, [])
            }
            itemWithTiming.timeStart = timeStringFromTimestamp(
                course.startTimestamp,
                this.studio.timezone
            )
            itemWithTiming.timeEnd = timeStringFromTimestamp(
                course.endTimestamp,
                this.studio.timezone
            )
            formattedObject.get(dateKey).push(itemWithTiming)
            return formattedObject
        },
        async getUpcomingClassesAndCourseSessions() {
            const [upcomingClasses, upcomingCourses] = await Promise.all([
                this.getUserBookings('upcoming', 'class'),
                this.getUserBookings('upcoming', 'course'),
            ])
            this.upcomingClasses = upcomingClasses
            this.upcomingCourses = upcomingCourses
        },
        async getPastClassesAndCourseSessions() {
            const [pastClasses, pastCourses] = await Promise.all([
                this.getUserBookings('past', 'class'),
                this.getUserBookings('past', 'course'),
            ])
            this.pastClasses = pastClasses
            this.pastCourses = pastCourses
        },
        async getMorePastClassesAndCourseSessions() {
            this.viewMoreLoading = true
            const result = await this.getUserBookings(
                'past',
                this.selectedView,
                true
            )
            this.viewMoreLoading = false
            if (this.selectedView === 'class') {
                this.pastClasses = this.mergeSessions(this.pastClasses, result)
            } else {
                this.pastCourses = this.mergeSessions(this.pastCourses, result)
            }
        },
        async mergeSessions(oldSessions, newSessions) {
            if (oldSessions.length === 0) {
                return newSessions
            } else if (newSessions.length === 0) {
                return oldSessions
            }

            const lastIndex = oldSessions.length - 1
            const lastItemDate = oldSessions[lastIndex].date

            newSessions.forEach((session) => {
                if (session.date === lastItemDate) {
                    oldSessions[lastIndex].item.push(...session.item)
                } else {
                    oldSessions.push(session)
                }
            })

            return oldSessions
        },
        async getWaitlistClasses() {
            this.waitlistedClasses = await this.getUserBookings(
                'waitlist',
                'class'
            )
        },
        async getCancelledClasses() {
            this.cancelledClasses = await this.getUserBookings(
                'cancelled',
                'class'
            )
        },
        handleCancelledSuccess() {
            this.upcomingClasses = {}
            this.getUpcomingClassesAndCourseSessions()
            if (this.cancelFeePaymentError !== '') {
                this.showInfoToast(this.cancelFeePaymentError)
            } else {
                this.showSuccessToast(
                    this.$t('message.emailConfirmCancellationSent')
                )
            }
            this.cancelFeePaymentError = ''
            this.refreshList()
        },
        handleCancelFeePaymentError(err) {
            this.cancelFeePaymentError = err
        },
        handleAssignFamilyUserSuccess() {
            this.upcomingClasses = {}
            this.getUpcomingClassesAndCourseSessions()
            this.showSuccessToast('Success!')
        },
        handleWaitlistSuccess() {
            this.waitlistedClasses = {}
            this.getWaitlistClasses()
            this.showSuccessToast(
                this.$t('message.emailConfirmRemovalFromWaitlistSent')
            )
        },
    },
}
</script>
